'use client';

import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiKitButtonBase } from '@/fo/ui/kit/button/base';

import { InputOTP } from '../components/InputOTP/InputOTP';
import { InputOTPGroup } from '../components/InputOTPGroup/InputOTPGroup';
import { InputOTPSlot } from '../components/InputOTPSlot/InputOTPSlot';
import { ResendCodeTimer } from '../components/ResendCodeTimer/ResendCodeTimer';
import { SendCodeResponseType } from '../types';

import { styles } from './UiAuthInputCode.styles';

type FormStatusType = {
  status: VariantProps<typeof styles>['status'];
  message?: string;
};

export interface UiAuthInputCodeProps {
  /**
   * Номер телефона, на который будет отправлен код подтверждения.
   * Должен быть в формате строки.
   */
  phoneNumber?: string;
  /** Функция для отправки кода подтверждения. */
  onSendCode: (code: string) => Promise<SendCodeResponseType>;
  /** Функция для повторной отправки кода. */
  onResend: () => Promise<void> | void;
  /** Максимальная длина кода. Определяет количество символов, которые можно ввести. (по умолчанию 6 символов) */
  maxLength?: number;
  /**
   * Регулярное выражение для валидации ввода кода.
   * По умолчанию `REGEXP_ONLY_DIGITS_AND_CHARS` из `input-otp`.
   */
  pattern?: string;

  onComplete?: () => void;
  /** При передаче сообщение отдаётся сюда вместо тоста; родитель может вывести FormMessage */
  onMessage?: (message: string, status: 'error' | 'success') => void;
}

export function UiAuthInputCode({
  onSendCode,
  onResend,
  maxLength = 4,
  pattern = REGEXP_ONLY_DIGITS_AND_CHARS,
  onMessage
}: UiAuthInputCodeProps) {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [formStatus, setFormStatus] = useState<FormStatusType>({
    status: 'default'
  });

  const slots = styles({ status: formStatus.status });

  const confirmNumber = async () => {
    if (value.length !== maxLength || formStatus.status === 'loading') return;
    inputRef.current?.blur();
    setFormStatus({ status: 'loading' });

    const {
      status: responseStatus,
      message,
      toastMessage
    } = await onSendCode(value);

    const displayMessage = toastMessage ?? message ?? '';
    if (onMessage && displayMessage) {
      onMessage(displayMessage, responseStatus);
    }

    if (responseStatus === 'error') {
      setFormStatus({ status: 'error', message });
      return;
    }

    setFormStatus({ status: 'completed', message });
  };

  return (
    <div className={slots.base()}>
      <InputOTP
        containerClassName={slots.container()}
        maxLength={maxLength}
        value={value}
        onChange={(value) => setValue(value)}
        pattern={pattern}
        ref={inputRef}
        onKeyDown={async (e) => {
          if (e.key === 'Enter') {
            await confirmNumber();
          }
        }}
      >
        <InputOTPGroup className={slots.inputGroup()}>
          {Array.from({ length: maxLength }).map((_, i) => (
            <InputOTPSlot
              key={i}
              index={i}
              className={slots.inputText()}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      {!onMessage && (
        <span className={slots.helperText()}>{formStatus.message}</span>
      )}
      <ResendCodeTimer
        className={slots.resendText()}
        onResend={onResend}
      />
      <UiKitButtonBase
        type="submit"
        color="secondary"
        loading={formStatus.status === 'loading'}
        onClick={() => confirmNumber()}
        disabled={value.length !== maxLength || formStatus.status === 'loading'}
      >
        {t('Confirm number')}
      </UiKitButtonBase>
      {formStatus.status === 'loading' && (
        <div className={slots.loadingContainer()}>
          <span className={slots.loading()} />
        </div>
      )}
    </div>
  );
}
